import { createSlice, createAsyncThunk, PayloadAction, createAction } from '@reduxjs/toolkit';
import type {
  Content,
  ContentStatus,
  SortField,
  ContentIngestJobMeta,
  ContentIngestEvent,
  ContentIngestJobState,
  ContentListMeta
} from '@/types/content';
import type { ContentState } from '@/types/content.d';
import { RootState } from '@/store/store';
import { contentService } from '@/api/services/contentService';
const initialState: ContentState = {
  items: [],
  loading: false,
  error: null,
  selectedContentId: null,
  selectedContent: null,
  selectedIds: [],
  searchQuery: '',
  lastFetchTime: null,
  statusFilter: null,
  categoryFilter: null,
  chatbotFilter: null,
  ingestJobs: {},
  meta: {
    total: 0,
    page: 1,
    limit: 20,
    hasNext: false,
    hasPrev: false,
    textTotal: 0,
    fileTotal: 0,
    urlTotal: 0
  },
  filters: {
    status: null,
    category: null,
    chatbot: null
  },
  sort: {
    field: 'lastUpdated',
    direction: 'desc'
  },
  search: {
    query: '',
    suggestions: [],
    history: []
  }
};

export const ingestJobRegistered = createAction<ContentIngestJobState>('content/ingestJobRegistered');
export const ingestJobEventReceived = createAction<{ jobId: string; event: ContentIngestEvent }>('content/ingestJobEventReceived');
export const ingestJobCompleted = createAction<{ jobId: string; event: ContentIngestEvent }>('content/ingestJobCompleted');
export const ingestJobFailed = createAction<{ jobId: string; event: ContentIngestEvent }>('content/ingestJobFailed');
export const clearIngestJob = createAction<{ jobId: string }>('content/clearIngestJob');

const stagePercentageHints: Record<string, number> = {
  received: 2,
  connected: 2,
  queued: 5,
  extracting_text: 10,
  extracting_text_progress: 15,
  text_extracted: 18,
  chunking_started: 20,
  chunking_completed: 60,
  embedding_started: 60,
  embedding_completed: 93,
  storing_started: 95,
  storing_completed: 97,
  ready: 100
};

const stageProgressRanges: Record<string, [number, number]> = {
  extracting_text_progress: [10, 18],
  chunking_progress: [20, 60],
  embedding_progress: [60, 93]
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const mapStageProgress = (raw: number | undefined, stage: string): number | undefined => {
  if (raw === undefined) {
    return undefined;
  }
  const range = stageProgressRanges[stage];
  if (!range) {
    return clamp(raw, 0, 100);
  }
  const [start, end] = range;
  const normalized = clamp(raw, 0, 100);
  const spread = end - start;
  if (spread <= 0) {
    return end;
  }
  return start + (normalized / 100) * spread;
};

const updateJobProgressFromEvent = (job: ContentIngestJobState, event: ContentIngestEvent) => {
  if (!event || typeof event !== 'object') {
    return;
  }

  const stageRaw = (event as any).stage;
  if (typeof stageRaw !== 'string' || stageRaw.length === 0) {
    return;
  }

  const stage = stageRaw.toLowerCase();
  const payload = event as Record<string, unknown>;
  const hint = stagePercentageHints[stage];

  const nextProgress: Record<string, unknown> = {
    ...(job.progress || {})
  };

  let percentage = coerceNumber((payload as any).progress_pct);
  const mappedPercentage = mapStageProgress(percentage, stage);

  const applyPercentage = (value: number | undefined) => {
    if (value === undefined) {
      if (hint !== undefined) {
        const current = coerceNumber((nextProgress as any).percentage) ?? 0;
        (nextProgress as any).percentage = Math.max(current, hint);
      }
      return;
    }
    const current = coerceNumber((nextProgress as any).percentage) ?? 0;
    (nextProgress as any).percentage = Math.max(current, clamp(value, 0, 100));
  };

  switch (stage) {
    case 'chunking_progress': {
      const splitsProcessed = coerceNumber((payload as any).splits_processed);
      const totalSplits = coerceNumber((payload as any).total_splits);
      const progressValue = mappedPercentage ?? hint;
      applyPercentage(progressValue);
      const chunking = {
        ...(nextProgress.chunking as Record<string, unknown> | undefined),
        processed: splitsProcessed ?? (nextProgress.chunking as any)?.processed,
        total: totalSplits ?? (nextProgress.chunking as any)?.total
      };
      nextProgress.chunking = chunking;
      nextProgress.phase = 'chunking';
      break;
    }
    case 'embedding_progress': {
      const chunksEmbedded = coerceNumber((payload as any).chunks_embedded);
      const totalChunks = coerceNumber((payload as any).total_chunks);
      const progressValue = mappedPercentage ?? hint;
      applyPercentage(progressValue);
      const embedding = {
        ...(nextProgress.embedding as Record<string, unknown> | undefined),
        processed: chunksEmbedded ?? (nextProgress.embedding as any)?.processed,
        total: totalChunks ?? (nextProgress.embedding as any)?.total
      };
      nextProgress.embedding = embedding;
      nextProgress.phase = 'embedding';
      break;
    }
    default: {
      const progressValue = mappedPercentage ?? hint;
      applyPercentage(progressValue);
      nextProgress.phase = stage;
      break;
    }
  }

  if (stage.endsWith('_completed') || stage === 'ready') {
    applyPercentage(stage === 'ready' ? 100 : hint ?? mappedPercentage);
  }

  nextProgress.raw = payload;
  job.progress = nextProgress as typeof job.progress;
};

const buildInitialIngestJobState = (job: ContentIngestJobMeta): ContentIngestJobState => ({
  ...job,
  stage: job.status,
  events: [],
  lastEvent: undefined,
  progress: job.status ? { percentage: stagePercentageHints[job.status] ?? 0, phase: job.status } : undefined,
  error: undefined,
  completed: false,
  completedAt: undefined,
  updatedAt: job.submittedAt
});

// Create async thunk for fetching content
export const fetchContents = createAsyncThunk<
  { items: Content[]; meta: ContentListMeta },
  Record<string, any>,
  { rejectValue: string }
>(
  'content/fetchContents',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      console.log('ContentSlice: Fetching content from API');
      const data = await contentService.fetchContent(params);
      return data;
    } catch (error: any) {
      console.error('ContentSlice: Error fetching content', error);
      return rejectWithValue(error.message || 'Failed to fetch content');
    }
  }
);

// Content stats fetch thunk
export const fetchContentStats = createAsyncThunk(
  'content/fetchContentStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      console.log('ContentSlice: Starting fetch stats');
      
      // Get content from current state instead of mock data
      const state = getState() as any;
      const contentItems = state.content.items || [];
      
      // Calculate stats from actual content data
      const totalFiles = contentItems.filter((c: any) => c.type === 'file').length;
      const totalSnippets = contentItems.filter((c: any) => c.type === 'snippet').length;
      const totalWebsites = contentItems.filter((c: any) => c.type === 'website').length;
      const activeContent = contentItems.filter((c: any) => c.status === 'completed' || c.status === 'indexed').length;
      
      // Create category distribution
      const categoryMap = new Map();
      contentItems.forEach((content: any) => {
        const category = content.category || 'Uncategorized';
        const count = categoryMap.get(category) || 0;
        categoryMap.set(category, count + 1);
      });
      
      const topCategories = Array.from(categoryMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => (b.count as number) - (a.count as number))
        .slice(0, 5);
      
      console.log('ContentSlice: Stats fetch complete');
      
      return {
        totalFiles,
        totalSnippets,
        totalWebsites,
        activeContent,
        averageProcessingTime: 2.3, // Could be calculated from API or set to reasonable default
        topCategories
      };
    } catch (error: any) {
      console.error('ContentSlice: Error fetching stats', error);
      return rejectWithValue(error.message || 'Failed to fetch content statistics');
    }
  }
);

// Fetch single content by ID
export const fetchContentById = createAsyncThunk(
  'content/fetchContentById',
  async (id: string, { rejectWithValue }) => {
    try {
      console.log('ContentSlice: Starting fetchContentById for ID:', id);
      const content = await contentService.fetchContentById(id);
      console.log('ContentSlice: Successfully fetched content:', content.id, content.title);
      return content;
    } catch (error: any) {
      console.error('ContentSlice: Error fetching content by ID:', id, error);
      return rejectWithValue(error.message || 'Failed to fetch content details');
    }
  }
);

/**
 * When the backend completes ingestion synchronously (no stream_url),
 * skip SSE and immediately mark the job as completed + refresh the list.
 */
const handleIngestResult = (jobMeta: ContentIngestJobMeta, thunkAPI: any) => {
  const jobState = buildInitialIngestJobState(jobMeta);
  thunkAPI.dispatch(ingestJobRegistered(jobState));

  if (jobMeta.streamUrl) {
    contentService.subscribeToIngestStream(jobMeta, {
      onEvent: (event) => {
        thunkAPI.dispatch(ingestJobEventReceived({ jobId: jobMeta.jobId, event }));
      },
      onComplete: (event) => {
        thunkAPI.dispatch(ingestJobCompleted({ jobId: jobMeta.jobId, event }));
        const state = thunkAPI.getState() as RootState;
        const currentPage = state.content.meta?.page ?? 1;
        const currentLimit = state.content.meta?.limit ?? 20;
        thunkAPI.dispatch(fetchContents({ page: currentPage, limit: currentLimit }));
      },
      onError: (event) => {
        thunkAPI.dispatch(ingestJobFailed({ jobId: jobMeta.jobId, event }));
      }
    });
  } else {
    // Synchronous ingestion — already completed
    const completeEvent = { type: 'status', stage: 'ready', job_id: jobMeta.jobId };
    thunkAPI.dispatch(ingestJobCompleted({ jobId: jobMeta.jobId, event: completeEvent }));
    const state = thunkAPI.getState() as RootState;
    const currentPage = state.content.meta?.page ?? 1;
    const currentLimit = state.content.meta?.limit ?? 20;
    thunkAPI.dispatch(fetchContents({ page: currentPage, limit: currentLimit }));
  }

  return jobState;
};

export const createSnippet = createAsyncThunk(
  'content/createSnippet',
  async (content: Content, thunkAPI) => {
    try {
      console.log('ContentSlice: Starting create snippet ingestion');
      const jobMeta = await contentService.createSnippet(content);
      return handleIngestResult(jobMeta, thunkAPI);
    } catch (error: any) {
      console.error('ContentSlice: Error creating content', error);
      return thunkAPI.rejectWithValue(error.message || 'Failed to create content');
    }
  }
);

export const createFile = createAsyncThunk(
  'content/createFile',
  async (content: Content, thunkAPI) => {
    try {
      console.log('ContentSlice: Starting file ingestion');
      const jobMeta = await contentService.createFile(content);
      return handleIngestResult(jobMeta, thunkAPI);
    } catch (error: any) {
      console.error('ContentSlice: Error creating content', error);
      return thunkAPI.rejectWithValue(error.message || 'Failed to create content');
    }
  }
);

export const createWebsite = createAsyncThunk(
  'content/createWebsite',
  async (content: Content, thunkAPI) => {
    try {
      console.log('ContentSlice: Starting website ingestion');
      const jobMeta = await contentService.createWebsite(content);
      return handleIngestResult(jobMeta, thunkAPI);
    } catch (error: any) {
      console.error('ContentSlice: Error creating content', error);
      return thunkAPI.rejectWithValue(error.message || 'Failed to create content');
    }
  }
);

export const deleteContentsByIds = createAsyncThunk(
  'content/deleteContentsByIds',
  async (contentIds: string[], thunkAPI) => {
    try {
      await contentService.deleteContent(contentIds);
      thunkAPI.dispatch(deleteContents(contentIds));
      return contentIds;
    } catch (error: any) {
      console.error('ContentSlice: Error deleting content', error);
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete content');
    }
  }
);

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setContents: (state, action: PayloadAction<Content[]>) => {
      state.items = action.payload;
      state.lastFetchTime = Date.now();
      state.loading = false;
    },
    selectContent: (state, action: PayloadAction<string>) => {
      state.selectedContentId = action.payload;
      state.selectedContent = state.items.find(item => item.id === action.payload) || null;
    },
    clearSelectedContent: (state) => {
      state.selectedContentId = null;
      state.selectedContent = null;
    },
    toggleContentSelection: (state, action: PayloadAction<string>) => {
      const contentId = action.payload;
      if (state.selectedIds.includes(contentId)) {
        state.selectedIds = state.selectedIds.filter(id => id !== contentId);
      } else {
        state.selectedIds.push(contentId);
      }
    },
    selectAllContents: (state) => {
      state.selectedIds = state.items.map(item => item.id);
    },
    clearSelection: (state) => {
      state.selectedIds = [];
    },
    setStatusFilter: (state, action: PayloadAction<ContentStatus | null>) => {
      state.statusFilter = action.payload;
      state.filters.status = action.payload;
    },
    setCategoryFilter: (state, action: PayloadAction<string | null>) => {
      state.categoryFilter = action.payload;
      state.filters.category = action.payload;
    },
    setChatbotFilter: (state, action: PayloadAction<string | null>) => {
      state.chatbotFilter = action.payload;
      state.filters.chatbot = action.payload;
    },
    setFilter: (state, action: PayloadAction<{ key: keyof typeof state.filters; value: string | null }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value as ContentStatus | null;
      if (key === 'status') state.statusFilter = value as ContentStatus | null;
      if (key === 'category') state.categoryFilter = value;
      if (key === 'chatbot') state.chatbotFilter = value;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        category: null,
        chatbot: null
      };
      state.statusFilter = null;
      state.categoryFilter = null;
      state.chatbotFilter = null;
    },
    setSortField: (state, action: PayloadAction<SortField>) => {
      state.sort.field = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sort.direction = action.payload;
    },
    setSort: (state, action: PayloadAction<{ field: SortField; direction: 'asc' | 'desc' }>) => {
      state.sort = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      const query = action.payload;
      state.search.query = query;
      state.searchQuery = query;
      
      if (query && !state.search.history.includes(query)) {
        state.search.history = [query, ...state.search.history].slice(0, 10);
      }
    },
    updateSearchSuggestions: (state, action: PayloadAction<string[]>) => {
      state.search.suggestions = action.payload;
    },
    clearSearch: (state) => {
      state.search.query = '';
      state.searchQuery = '';
      state.search.suggestions = [];
    },
    clearSearchHistory: (state) => {
      state.search.history = [];
    },
    updateContent: (state, action: PayloadAction<{ id: string; updates: Partial<Content> }>) => {
      const { id, updates } = action.payload;
      
      const contentIndex = state.items.findIndex(item => item.id === id);
      if (contentIndex !== -1) {
        state.items[contentIndex] = {
          ...state.items[contentIndex],
          ...updates,
          lastUpdated: new Date().toISOString()
        };
      }
      
      if (state.selectedContentId === id && state.selectedContent) {
        state.selectedContent = {
          ...state.selectedContent,
          ...updates,
          lastUpdated: new Date().toISOString()
        };
      }
    },
    updateContentStatus: (state, action: PayloadAction<{ ids: string[]; status: ContentStatus }>) => {
      const { ids, status } = action.payload;
      
      ids.forEach(id => {
        const contentIndex = state.items.findIndex(item => item.id === id);
        if (contentIndex !== -1) {
          state.items[contentIndex].status = status;
          state.items[contentIndex].lastUpdated = new Date().toISOString();
        }
      });
      
      if (state.selectedContent && ids.includes(state.selectedContent.id)) {
        state.selectedContent.status = status;
        state.selectedContent.lastUpdated = new Date().toISOString();
      }
    },
    reassignChatbot: (state, action: PayloadAction<{ ids: string[]; chatbotId: string; chatbotName: string }>) => {
      const { ids, chatbotId, chatbotName } = action.payload;
      
      ids.forEach(id => {
        const contentIndex = state.items.findIndex(item => item.id === id);
        if (contentIndex !== -1) {
          if (!state.items[contentIndex].chatbots) {
            state.items[contentIndex].chatbots = [];
          }

          const existingChatbot = state.items[contentIndex].chatbots?.find(bot => bot.id === chatbotId);
          if (!existingChatbot) {
            state.items[contentIndex].chatbots?.push({ id: chatbotId, name: chatbotName });
          }
          
          state.items[contentIndex].lastUpdated = new Date().toISOString();
        }
      });
      
      if (state.selectedContent && ids.includes(state.selectedContent.id)) {
        if (!state.selectedContent.chatbots) {
          state.selectedContent.chatbots = [];
        }
        
        const existingChatbot = state.selectedContent.chatbots.find(bot => bot.id === chatbotId);
        if (!existingChatbot) {
          state.selectedContent.chatbots.push({ id: chatbotId, name: chatbotName });
        }
        
        state.selectedContent.lastUpdated = new Date().toISOString();
      }
    },
    deleteContents: (state, action: PayloadAction<string[]>) => {
      const idsToDelete = action.payload;
      state.items = state.items.filter(item => !idsToDelete.includes(item.id));
      
      if (state.selectedContentId && idsToDelete.includes(state.selectedContentId)) {
        state.selectedContentId = null;
        state.selectedContent = null;
      }
      
      state.selectedIds = state.selectedIds.filter(id => !idsToDelete.includes(id));
    },
    addContent: (state, action: PayloadAction<Content>) => {
      state.items.unshift(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContents.pending, (state) => {
        console.log('ContentSlice: Fetch pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContents.fulfilled, (state, action) => {
        console.log('ContentSlice: Fetch fulfilled with', action.payload.items.length, 'items');
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.lastFetchTime = Date.now();
        state.error = null;
      })
      .addCase(fetchContents.rejected, (state, action) => {
        console.log('ContentSlice: Fetch rejected', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Content By ID
      .addCase(fetchContentById.pending, (state) => {
        console.log('ContentSlice: Fetch by ID pending - setting loading to true');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContentById.fulfilled, (state, action) => {
        console.log('ContentSlice: Fetch by ID fulfilled for:', action.payload.id, action.payload.title);
        state.loading = false;
        // Update existing item or add if not exists
        const existingIndex = state.items.findIndex(item => item.id === action.payload.id);
        if (existingIndex >= 0) {
          console.log('ContentSlice: Updating existing content at index:', existingIndex);
          state.items[existingIndex] = action.payload;
        } else {
          console.log('ContentSlice: Adding new content to store');
          state.items.push(action.payload);
        }
      })
      .addCase(fetchContentById.rejected, (state, action) => {
        console.log('ContentSlice: Fetch by ID rejected with error:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(ingestJobRegistered, (state, action) => {
        state.ingestJobs[action.payload.jobId] = action.payload;
      })
      .addCase(ingestJobEventReceived, (state, action) => {
        const job = state.ingestJobs[action.payload.jobId];
        if (!job) return;

        const event = action.payload.event;
        job.updatedAt = new Date().toISOString();
        job.events.push(event);
        if (job.events.length > 200) {
          job.events.shift();
        }
        job.lastEvent = event;

        updateJobProgressFromEvent(job, event);

        if (event.type === 'connected') {
          const current = coerceNumber(job.progress?.percentage) ?? 0;
          const hint = stagePercentageHints.connected ?? 2;
          job.progress = {
            ...(job.progress || {}),
            percentage: Math.max(current, hint),
            phase: 'connected',
            raw: event as Record<string, unknown>
          };
        }

        if ('stage' in event && typeof event.stage === 'string') {
          job.stage = event.stage;
          job.status = event.stage;
        }

        if (event.type === 'status' && 'status' in event && typeof event.status === 'string') {
          job.status = event.status;
        }
      })
      .addCase(ingestJobCompleted, (state, action) => {
        const job = state.ingestJobs[action.payload.jobId];
        if (!job) return;

        job.completed = true;
        job.completedAt = new Date().toISOString();
        if ('stage' in action.payload.event && typeof action.payload.event.stage === 'string') {
          job.stage = action.payload.event.stage;
        }
        job.status = 'ready';
        const current = coerceNumber(job.progress?.percentage) ?? 0;
        job.progress = {
          ...(job.progress || {}),
          percentage: Math.max(current, 100),
          phase: 'ready'
        };
      })
      .addCase(ingestJobFailed, (state, action) => {
        const job = state.ingestJobs[action.payload.jobId];
        if (!job) return;

        job.completed = true;
        job.completedAt = new Date().toISOString();
        job.status = 'error';
        if ('stage' in action.payload.event && typeof action.payload.event.stage === 'string') {
          job.stage = action.payload.event.stage;
        }
        job.error = {
          message: (action.payload.event as any).message || 'Ingestion failed',
          stage: (action.payload.event as any).stage,
          details: action.payload.event
        };
        const hint = typeof (action.payload.event as any).stage === 'string'
          ? stagePercentageHints[(action.payload.event as any).stage.toLowerCase()]
          : undefined;
        const current = coerceNumber(job.progress?.percentage) ?? 0;
        job.progress = {
          ...(job.progress || {}),
          percentage: hint !== undefined ? Math.max(current, hint) : current,
          phase: 'error'
        };
      })
      .addCase(clearIngestJob, (state, action) => {
        delete state.ingestJobs[action.payload.jobId];
      })
      // Create Snippet
      .addCase(createSnippet.pending, (state) => {
        console.log('ContentSlice: Create snippet pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(createSnippet.fulfilled, (state, action) => {
        console.log('ContentSlice: Create snippet fulfilled', action.payload);
        state.loading = false;
        // Add the new content to the beginning of the list
        // state.items.unshift(action.payload);
        // state.lastFetchTime = Date.now();
      })
      .addCase(createSnippet.rejected, (state, action) => {
        console.log('ContentSlice: Create snippet rejected', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create File
      .addCase(createFile.pending, (state) => {
        console.log('ContentSlice: Create file pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(createFile.fulfilled, (state, action) => {
        console.log('ContentSlice: Create file fulfilled', action.payload);
        state.loading = false;
        // Add the new content to the beginning of the list
        // state.items.unshift(action.payload);
        // state.lastFetchTime = Date.now();
      })
      .addCase(createFile.rejected, (state, action) => {
        console.log('ContentSlice: Create file rejected', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Website
      .addCase(createWebsite.pending, (state) => {
        console.log('ContentSlice: Create website pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(createWebsite.fulfilled, (state, action) => {
        console.log('ContentSlice: Create website fulfilled', action.payload);
        state.loading = false;
        // Add the new content to the beginning of the list
        // state.items.unshift(action.payload);
        // state.lastFetchTime = Date.now();
      })
      .addCase(createWebsite.rejected, (state, action) => {
        console.log('ContentSlice: Create website rejected', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setLoading,
  setError,
  setContents,
  selectContent,
  clearSelectedContent,
  toggleContentSelection,
  selectAllContents,
  clearSelection,
  setStatusFilter,
  setCategoryFilter,
  setChatbotFilter,
  setFilter,
  clearFilters,
  setSortField,
  setSortDirection,
  setSort,
  setSearchQuery,
  updateSearchSuggestions,
  clearSearch,
  clearSearchHistory,
  updateContent,
  updateContentStatus,
  reassignChatbot,
  deleteContents,
  addContent
} = contentSlice.actions;

// Selectors
export const selectContentState = (state: RootState) => state.content;
export const selectAllContentItems = (state: RootState) => state.content.items;
export const selectContentLoading = (state: RootState) => state.content.loading;
export const selectContentError = (state: RootState) => state.content.error;
export const selectSelectedContent = (state: RootState) => state.content.selectedContent;
export const selectSelectedContentId = (state: RootState) => state.content.selectedContentId;
export const selectSelectedIds = (state: RootState) => state.content.selectedIds;
export const selectContentFilters = (state: RootState) => state.content.filters;
export const selectContentSort = (state: RootState) => state.content.sort;
export const selectContentSearchQuery = (state: RootState) => state.content.searchQuery;
export const selectContentSearchData = (state: RootState) => state.content.search;
export const selectLastFetchTime = (state: RootState) => state.content.lastFetchTime;

export const contentReducer = contentSlice.reducer;
